import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { ExternalLinkIcon } from '@heroicons/react/solid';
import Countdown from './Countdown';
import Spinner from './Spinner';
import Button from './Button';
import useRaffleOwner from '../hooks/useRaffleOwner';
import { useAlgo } from '../hooks/useAlgo';
import { useRaffles } from '../hooks/useRaffles';
import formatNumber from '../utils/formatNumber';
import get from 'lodash/get';
import useNfd from '../hooks/useNfd';
import Link from 'next/link';

export function Detail({ label, value, children, success }) {
  return (
    <p className='flex flex-col overflow-scroll'>
      <span className='text-sm font-bold text-text opacity-60'>{label}</span>{' '}
      <span className={clsx('font-bold', success ? 'text-green-500' : 'text-text')}>{children || value}</span>
    </p>
  );
}

function RafflePreview({ raffle }) {
  const { id, assetId, endDate } = raffle;
  const raffleOwner = useRaffleOwner(raffle.owner);
  const { address } = useAlgo();
  const { getEntries, setBalance, balance } = useRaffles();
  const [numberOfTickets, setNumberOfTickets] = useState(0);
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [setEntriesLoading, setSetEntriesLoading] = useState(false);
  const { getNfd } = useNfd();
  const [winner, setWinner] = useState(null);

  const formattedEntries = entries
    ?.map((entry) => {
      return {
        from: entry.transaction.sender,
        amount: entry.tickets,
      };
    })
    .reduce((acc, curr) => {
      const { sender, amount } = curr;
      if (!acc[sender]) {
        acc[sender] = 0;
      }
      acc[sender] += amount;
      return acc;
    }, {});

  // sort formattedEntries by value
  const sortedEntries = Object.keys(formattedEntries)
    .map((key) => {
      return {
        address: key,
        amount: formattedEntries[key],
      };
    })
    .sort((a, b) => b.amount - a.amount);

  const numberOfEntries = sortedEntries.reduce((acc, curr) => {
    return acc + curr.amount;
  }, 0);

  const numberOfYourEntries = sortedEntries.reduce((acc, curr) => {
    return acc + (curr.address === address ? curr.amount : 0);
  }, 0);

  useEffect(() => {
    if (!raffle.winner) return;

    (async () => {
      try {
        const nfd = await getNfd(raffle.winner);
        if (nfd) {
          return setWinner(get(nfd, 'name'));
        }
        setWinner(`${raffle.winner?.slice(0, 4)}...${raffle.winner?.slice(-4)}`);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [raffle.winner]);

  useEffect(() => {
    (async function () {
      try {
        setSetEntriesLoading(true);
        const them = await getEntries(id);
        setEntries(them);
      } catch (error) {
        console.error(error);
      } finally {
        setSetEntriesLoading(false);
      }
    })();
  }, [address]);

  return (
    <div>
      <div className='relative flex items-center justify-center w-full overflow-hidden rounded-t-lg aspect-square bg-neutral-900'>
        <Link href={`/raffles/${raffle.id}`}>
          <a className='block'>
            <motion.img src={raffle.image} alt={raffle.name} className='absolute w-full h-full inset-0 z-10 object-cover rounded-t-lg' />
          </a>
        </Link>
        <Spinner dimensionClasses='w-8 h-8' />
      </div>

      <div className='relative col-span-12 md:col-span-6 lg:col-span-7'>
        <div className='relative px-3 py-4 rounded-b-lg bg-neutral-900 text-neutral-200'>
          <div className='flex flex-col space-y-3'>
            <div>
              <h2 className='mb-1 text-sm font-bold text-text opacity-60'>{raffle.collection}</h2>
              <h3 className='flex items-center mb-1 space-x-3 text-lg font-bold'>
                <span>{raffle.name}</span>
                <span>
                  <a href={`https://www.downbad.farm/asset/${raffle.assetId}`} target='_blank' rel='noreferrer'>
                    <div className='w-6 opacity-50 text-text'>
                      <ExternalLinkIcon />
                    </div>
                  </a>
                </span>
              </h3>
            </div>

            <div>
              <p className='flex flex-col'>
                <span className='text-sm font-bold text-text opacity-60'>Hosted By</span>{' '}
                <a href={raffleOwner.link} className={clsx('font-bold text-text')} style={{ color: raffleOwner.color }}>
                  {raffleOwner.collection}
                </a>
              </p>
            </div>

            <div className='grid grid-cols-3 gap-2'>
              <Detail label='Ticket cost' value={`${raffle.ticketCost} ${raffleOwner.tokenAbbr}`} />
              <Detail
                label='Total Entries'
                value={
                  raffle.totalTickets
                    ? `${formatNumber(numberOfEntries) || 0}/${formatNumber(raffle.totalTickets)}`
                    : formatNumber(numberOfEntries) || 0
                }
              />
              {address && (
                <Detail
                  label='My Entries'
                  value={raffle.maxEntries ? `${numberOfYourEntries || 0}/${formatNumber(raffle.maxEntries)}` : numberOfYourEntries || 0}
                />
              )}
            </div>

            <div className={`grid ${winner ? 'grid-cols-2' : 'grid-cols-1'}`}>
              <div>
                <span className='text-sm font-bold text-text opacity-60'>Ends in</span>
                {new Date(endDate) < new Date() ? (
                  <div className='font-bold text-green-500'>Raffle has ended</div>
                ) : (
                  <div className='font-bold'>
                    <Countdown date={new Date(endDate)} simple />
                  </div>
                )}
              </div>
            </div>

            {winner && (
              <div>
                <Detail label='Winner' value={winner} success />
              </div>
            )}
          </div>
        </div>

        <div className='flex mt-6'>
          <Button className='!w-full !flex justify-center' href={`/raffles/${raffle.id}`} link>
            {loading ? <Spinner dimensionClasses='w-4 h-4' /> : <span>View Raffle</span>}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default RafflePreview;
